import { useState, useCallback } from 'react';
import { t } from '@lingui/macro';
import { useToast } from '@pocketlaw/tetris';
import T from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';

import { FileSystemManager, useS3FileUpload } from 'app/domains/filesystem';
import { useAutoTagDocuments } from 'app/domains/metadata';
import { EntryKind } from 'shared/domains/apollo/generated/types';
import * as Sentry from 'shared/domains/sentry';

import { useCreateDocument } from './useCreateDocument';

const nameFromFile = file => file.name.slice(0, file.name.lastIndexOf('.'));

export function UploadDocumentDialog({
  files: controlledFiles,
  onCreated,
  onClose,
  withRedirect = true,
}) {
  const { folderId } = useParams();
  const [files, setFiles] = useState(controlledFiles);
  const { upload } = useS3FileUpload();
  const [createDocument] = useCreateDocument({ onCreated });
  const { addToast } = useToast();
  const history = useHistory();

  const autoTag = useAutoTagDocuments();

  const type = EntryKind.Document;

  const handleSubmit = useCallback(
    (values, { resetForm, setSubmitting }) => {
      upload(files.slice(0, 1))
        .then(results => {
          const result = results[0];

          if (!result.success) {
            throw result.reason;
          }

          const file = result.uploadedFile;
          return createDocument({ ...values, file });
        })
        .then(async ({ data }) => {
          addToast({ title: t`Document uploaded`, appearance: 'success' });
          const nextFiles = files.slice(1);
          const { document } = data || {};

          if (values.autoTag === true && document.id) {
            await autoTag.mutate([document.id]);
          }

          if (nextFiles.length > 0) {
            setFiles(nextFiles);
            resetForm({ values: { ...values, name: nameFromFile(nextFiles[0]) } });
          } else {
            const onCloseData = Array.isArray(data) ? data[0] : data;
            onClose(onCloseData);

            if (withRedirect && values.parentId) {
              history.push(`/files/${values.parentId}`);
            } else if (withRedirect) {
              history.push(`/files`);
            }
          }
        })
        .catch(error => {
          setSubmitting(false);
          Sentry.captureException(error);

          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureMessage('Failed to create document from uploaded file.');
          });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upload, createDocument, files, addToast, onClose]
  );

  return (
    <FileSystemManager
      entryType={type}
      defaultParentId={folderId}
      defaultName={nameFromFile(files[0])}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
}

UploadDocumentDialog.propTypes = {
  onCreated: T.func,
  onClose: T.func.isRequired,
  files: T.arrayOf(T.instanceOf(File)).isRequired,
  withRedirect: T.bool,
};

UploadDocumentDialog.defaultProps = {
  onCreated: undefined,
  withRedirect: true,
};
